<!-- 
/** 
 * Table paginator 
 * Muestra con varias opciones de configuración incluyendo muestra de visualización booleana compatible com tema.
 * 
 * @Author Daniel Lepe
 * @Version 1.0
 */-->

<template>
	<b-card class="table-paginator" no-body>
		<template v-slot:header>
			<b-row align-h="between">
				<b-col md="auto" v-if="!!caption">
					<h6 class="mt-2 mb-2">{{caption}}</h6>
				</b-col>
				<b-col>
					<div tabindex="-1" role="group">
						<div role="group" class="input-group">
							<div class="input-group-prepend">
								<button type="button" class="btn btn-primary" @click="refresh()">
									<i class="fa fa-search"></i> Search
								</button>
							</div>
							<input
								type="text"
								v-model="term"
								@keyup.enter="refresh()"
								placeholder="Name, e-mail, phone, etc..."
								class="form-control"
							/>
						</div>
					</div>
				</b-col>
				<b-col lg="2" md="3" sm="4" class="align-right" v-if="newItemName.length">
					<b-button variant="primary" block :to="newItem">
						<i class="fa fa-plus"></i> Nuevo
					</b-button>
				</b-col>
			</b-row>
		</template>

		<spinner v-if="loading" />

		<template v-else>
			<b-table class="animated fadeIn" responsive :dark="dark" :hover="hover" :striped="striped" :bordered="bordered" :small="small" :fixed="fixed" :items="items" :fields="captions" :per-page="perPage" >

				<!-- Start of avatar samples -->
				<template #cell(imagenes_id)="data">
					<api-img :comp="data.value" :rowed="true" />
				</template>

				<template #cell(imagenes_id_portadacategoria)="data">
					<api-img :comp="data.value" :rowed="true" />
				</template>

				<template #cell(imagenes_id_logo)="data">
					<api-img :comp="data.value" :rowed="true" />
				</template>

				<template #cell(imagenes_id_avatar)="data">
					<api-img :comp="data.value" :rowed="true" />
				</template>

				<template #cell(cliente_avatar)="data">
					<api-img :comp="data.value" :rowed="true" />
				</template>

				<template #cell(imagen_id)="data">
					<api-img :comp="data.value" :rowed="true" />
				</template>

				<template #cell(avatar)="data">
					<api-img :comp="data.value" :rowed="true" />
				</template>
				<!-- End if of avatar samples -->

				<!-- START OF SIMPLE FORMATS -->
				<template #cell(numero_orden)="data"> <strong>{{data.value}}</strong> </template>
				<template #cell(total)="data">{{data.value | currency}}</template>
				<template #cell(precio)="data">{{data.value | currency}}</template>
				<template #cell(created)="data">{{data.value | humanizar_fecha}}</template>
				<template #cell(modified)="data">{{data.value | humanizar_fecha}}</template>
				<!-- ENDF OF SIMPLE FORMATS -->

				<!-- Start of Boolean Sample -->
				<template #cell(disponible)="data">
					<div class="align-center">
						<i v-if="data.value == '1'" class="fa text-primary fa-check"></i>
						<i v-else class="fa fa-minus"></i>
					</div>
				</template>
				<!-- End of Boolean Sample -->

				<!-- START OF TOOLING -->
				<template #cell(_)="data">
					<router-link :to="composeEditUri(data.item)" class="btn btn-primary btn-sm btn-primary">
						Detail
					</router-link>
				</template>
				<!-- END OF TOOLING -->
			</b-table>

			<nav class="pb-1">
				<b-pagination v-model="currentPage" :total-rows="itemsCount" :per-page="perPage" align="center" prev-text="Back" next-text="Next" />
			</nav>

			<hr />

			<p class="text-center pt-3">
				<strong>Page {{currentPage}} of {{Math.ceil(itemsCount / perPage)}}</strong> 
				(Showing
				<strong>{{items.length}} records</strong>
				from {{itemsCount}} in total)
			</p>
		</template>
	</b-card>
</template>

<script>
import apiImg from "@/views/elements/api-img";
import axios from "axios";

export default {
	name: "table-paginator",
	inheritAttrs: false,
	components: {
		apiImg,
	},
	props: {
		newItemName: {
			String,
			default: "",
		},
		editName: {
			String,
			default: "",
		},
		caption: {
			type: String,
			default: null,
		},
		hover: {
			type: Boolean,
			default: false,
		},
		striped: {
			type: Boolean,
			default: false,
		},
		bordered: {
			type: Boolean,
			default: false,
		},
		small: {
			type: Boolean,
			default: false,
		},
		fixed: {
			type: Boolean,
			default: false,
		},
		tableUri: {
			type: String,
			default: () => "",
		},
		fields: {
			type: [Array, Object],
			default: () => [],
		},
		perPage: {
			type: Number,
			default: 25,
		},
		dark: {
			type: Boolean,
			default: false,
		}
	},
	data: () => {
		return {
			currentPage: 1,
			items: [],
			itemsCount: 0,
			loading: false,
			term: null,
		};
	},
	computed: {
		sourceURI() {
			let init =
				this.tableUri + "/" + this.perPage + "/" + this.currentPage;
			if (!!this.term) {
				init += "/" + this.term;
			} else if (!this.term || this.term == "s") {
				init += "/@";
			}
			return init;
		},
		captions: function () {
			return this.fields;
		},
		newItem() {
			return {
				name: this.newItemName,
			};
		},
	},
	mounted() {
		this.refresh()
	},
	methods: {
		composeEditUri(item) {
			return {
				name: this.editName,
				params: item,
			};
		},
		refresh() {
			this.items = [];
			this.loading = true;
			axios.get(this.sourceURI).then((response) => {
				this.items = response.data.rows;
				this.itemsCount = response.data.count;
				this.loading = false;
			});
		},
	},
	watch: {
		currentPage(a, b) {
			this.refresh();
		},
	},
};
</script>

<style lang="less">
.table-paginator {
	.api-img {
		max-width: 7em;
		max-height: 7em;
		object-fit: contain;
	}
	th {
		text-align: center;
	}
	.align-center {
		display: block;
		text-align: center;
	}
	.align-right {
		display: block;
		text-align: right;
		a {
			text-decoration: none;
		}
	}
}
</style>